import { ReactNode, useEffect, useMemo, useState } from "react";
import { Box, Drawer, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
  AggregateVersion,
  AggregateVersionChange,
  AggregateVersionUser,
  BASE_TRIGGER_LABELS,
} from "@models/aggregateVersion.model";

interface Props {
  open: boolean;
  onClose: () => void;
  versions: AggregateVersion[];
  /** Drawer heading. */
  title?: string;
  subtitle?: string;
  emptyMessage?: string;
  /** Domain event names on top of the universal three (`ad_approved`, `pin_for_run`, …). */
  triggerLabels?: Record<string, string>;
  /** Extra line under the summary — ingest uses it for "Used by N runs". */
  renderMeta?: (version: AggregateVersion) => ReactNode;
  /**
   * Show only these sections. This is the "levels of history" design: the scheduler, budget and
   * campaign pages render THIS component filtered to their own slice rather than each owning a
   * version table. A version whose changes all filter out is dropped from the list, so a
   * budget-only view isn't padded with rows that say nothing about budget.
   */
  sections?: string[];
  /**
   * The merged timeline — config versions interleaved with background jobs (MPN-1334). When
   * given, the drawer offers a Changes / Activity switch instead of a second button somewhere
   * else. Omit it and the drawer is exactly the changes list, which is what ingest gets.
   */
  activity?: AggregateActivityEntry[];
}

/** A line on the Activity timeline: a version, or something the system did. */
export interface AggregateActivityEntry {
  kind: "version" | "job";
  id: number;
  at: string;
  /** What this entry is about, when one timeline covers many things. See AggregateVersion. */
  subject?: string | null;
  title: string;
  actor: AggregateVersionUser | null;
  trigger: string | null;
  version: AggregateVersion | null;
  job_type: string | null;
  job_status: string | null;
}

/**
 * Versions with something to say about `sections`. Exported so a caller's count badge and the
 * drawer's list cannot disagree — a button reading "7" over a list of two is worse than no count.
 *
 * A baseline survives the filter deliberately: it carries no changes by design, and dropping it
 * loses the row that says where history starts.
 */
export function filterBySections(
  versions: AggregateVersion[],
  sections?: string[]
): AggregateVersion[] {
  if (!sections?.length) return versions;
  const wanted = new Set(sections);
  return versions
    .map((v) => ({ ...v, changes: v.changes.filter((c) => wanted.has(c.section)) }))
    .filter((v) => v.changes.length > 0 || v.trigger === "baseline");
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Activity needs the time too: "the budget changed at 2pm and a publish ran at 3pm" is the
 *  whole question, and a date alone cannot order the two. */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${d.toLocaleTimeString(
    "en-US",
    { hour: "numeric", minute: "2-digit" }
  )}`;
}

// An accent border rather than a leading glyph. The glyph sat in a flex row that had to wrap for
// long values, so it broke onto a line of its own and read as an empty bullet above the change.
// The border carries the same added/removed/changed signal and cannot be orphaned by wrapping.
const ACCENT: Record<AggregateVersionChange["kind"], string> = {
  added: "#128a67",
  removed: "#e6533c",
  changed: "#c9cfdb",
};

// A version row must answer "who", and the author alone never does: the system user is the
// author of the rollout baseline, of every schema bump and of every derived change. Pair it with
// the trigger so "System" is never shown bare.
function attribution(
  v: { changed_by: AggregateVersionUser | null; trigger: string | null },
  labels: Record<string, string>,
): string {
  const name = v.changed_by?.name || "System";
  // Only the ingest file history carries this. There, both the customer and staff act on the
  // same file, and "by Seth Ferderer" does not say which side of that he was on — which is
  // precisely the question asked when a file was reprocessed by hand (MPN-1660). Plain text in
  // parentheses rather than a badge: this line is already one quiet string, and a coloured pill
  // beside every row would outweigh the change it describes.
  const side =
    v.changed_by?.internal === undefined ? "" : v.changed_by.internal ? " (internal)" : " (client)";
  const who = `${name}${side}`;
  const why = v.trigger ? labels[v.trigger] ?? v.trigger.replace(/_/g, " ") : "";
  return why ? `by ${who} · ${why}` : `by ${who}`;
}

function groupBySection(
  changes: AggregateVersionChange[]
): [string, AggregateVersionChange[]][] {
  const groups: [string, AggregateVersionChange[]][] = [];
  for (const c of changes) {
    const last = groups[groups.length - 1];
    if (last && last[0] === c.section) last[1].push(c);
    else groups.push([c.section, [c]]);
  }
  return groups;
}

/**
 * What a row is about, when one timeline covers many things — the ingest file history merges
 * every file in a population, and without this every row reads "Loaded" with no way to tell
 * which of 48 files it means.
 *
 * Above the title rather than beside it, and set in the monospace face: these are file names,
 * they differ by a few characters in the middle (`..._2026-08-23_...` vs `..._2026-08-24_...`),
 * and in a proportional face at the end of a sentence that difference is invisible.
 */
function Subject({ text }: { text: string }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 600,
        fontFamily: "monospace",
        color: "#845adf",
        wordBreak: "break-all",
        lineHeight: 1.4,
      }}
    >
      {text}
    </Typography>
  );
}

function Badge({ text, bg, fg }: { text: string; bg: string; fg: string }) {
  return (
    <Box sx={{ px: 0.75, borderRadius: 5, bgcolor: bg, display: "inline-block" }}>
      <Typography sx={{ fontSize: 10, fontWeight: 600, color: fg, whiteSpace: "nowrap" }}>
        {text}
      </Typography>
    </Box>
  );
}

function ChangeRow({ change }: { change: AggregateVersionChange }) {
  const invisible = change.whitespace_only || change.case_only;
  // A trailing space or a case flip is a real edit that HTML would collapse into looking
  // identical, so those values keep pre-wrap and a monospace face.
  const valueFace = invisible
    ? { whiteSpace: "pre-wrap" as const, fontFamily: "monospace" }
    : {};
  const split = !!change.field;

  return (
    <Box
      sx={{
        pl: 1.25,
        py: 0.25,
        borderLeft: `2px solid ${ACCENT[change.kind]}`,
      }}
    >
      {/* The field, then the values beneath it. Two levels of hierarchy carried by weight and
          colour rather than size, so a wrapped 60-character engagement name still reads as one
          value and not as a second field. */}
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: "#7c8598",
          lineHeight: 1.5,
          wordBreak: "break-word",
        }}
      >
        {split ? change.field : change.label}
      </Typography>

      {split && (change.old_display !== null || change.new_display !== null) && (
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap", mt: 0.125 }}>
          {change.old_display !== null && (
            <Typography
              sx={{
                fontSize: 12.5,
                color: "#9aa1ae",
                lineHeight: 1.45,
                wordBreak: "break-word",
                ...(change.new_display !== null ? { textDecoration: "line-through" } : {}),
                ...valueFace,
              }}
            >
              {change.old_display}
            </Typography>
          )}
          {change.old_display !== null && change.new_display !== null && (
            <Box component="span" sx={{ color: "#c9cfdb", fontSize: 12, flexShrink: 0 }}>
              →
            </Box>
          )}
          {change.new_display !== null && (
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 500,
                color: "#2f3542",
                lineHeight: 1.45,
                wordBreak: "break-word",
                ...valueFace,
              }}
            >
              {change.new_display}
            </Typography>
          )}
        </Box>
      )}

      {(invisible || change.system) && (
        <Box sx={{ display: "flex", gap: 0.5, mt: 0.375 }}>
          {invisible && (
            <Badge
              text={change.whitespace_only ? "whitespace only" : "case only"}
              bg="#fdf3e3"
              fg="#a8742a"
            />
          )}
          {change.system && <Badge text="now tracked" bg="#eef2fb" fg="#536485" />}
        </Box>
      )}
    </Box>
  );
}

function VersionDiff({ changes }: { changes: AggregateVersionChange[] }) {
  return (
    <Box sx={{ mt: 1.25, pt: 1.25, borderTop: "1px dashed #eceef2", display: "flex", flexDirection: "column", gap: 1.5 }}>
      {groupBySection(changes).map(([section, items], gi) => (
        <Box key={`${section}-${gi}`} sx={{ display: "flex", flexDirection: "column", gap: 0.875 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#a4aab6", textTransform: "uppercase", letterSpacing: 0.5 }}>
            {section}
          </Typography>
          {items.map((c, i) => (
            <ChangeRow key={i} change={c} />
          ))}
        </Box>
      ))}
    </Box>
  );
}

const JOB_STATUS_STYLE: Record<string, { bg: string; fg: string }> = {
  COMPLETED: { bg: "#e7f7f1", fg: "#128a67" },
  WARNING: { bg: "#fdf3e3", fg: "#a8742a" },
  COMPLETED_WITH_ERRORS: { bg: "#fdf3e3", fg: "#a8742a" },
  FAILED: { bg: "#fdecea", fg: "#c0392b" },
  CANCELLED: { bg: "#f1f2f6", fg: "#8c9097" },
};

function ActivityList({
  entries,
  labels,
}: {
  entries: AggregateActivityEntry[];
  labels: Record<string, string>;
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", overflowY: "auto" }}>
      {entries.map((e, i) => {
        const isJob = e.kind === "job";
        const status = e.job_status ? JOB_STATUS_STYLE[e.job_status] : undefined;
        return (
          <Box
            key={`${e.kind}-${e.id}`}
            sx={{
              display: "flex",
              gap: 1.25,
              py: 1.25,
              borderTop: i === 0 ? "none" : "1px solid #f3f4f7",
            }}
          >
            {/* A rail, so a publish is distinguishable from a config change at a glance
                without reading either line. */}
            <Box sx={{ pt: 0.375, flexShrink: 0 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: isJob ? "#845adf" : "#c9cfdb",
                }}
              />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {e.subject && <Subject text={e.subject} />}
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 12.5, color: "#333335", wordBreak: "break-word" }}>
                  {e.title}
                </Typography>
                {status && (
                  <Badge text={e.job_status!.replace(/_/g, " ").toLowerCase()} bg={status.bg} fg={status.fg} />
                )}
              </Box>
              <Typography sx={{ fontSize: 11.5, color: "#8c9097", mt: 0.125 }}>
                {formatDateTime(e.at)} · {attribution({ changed_by: e.actor, trigger: e.trigger }, labels)}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default function AggregateVersionsDrawer({
  open,
  onClose,
  versions,
  title = "Config history",
  subtitle,
  emptyMessage = "Nothing recorded yet. Saving creates the first entry.",
  triggerLabels,
  renderMeta,
  sections,
  activity,
}: Props) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  // Scoped by default when the caller passes sections, but never a dead end: a version that
  // touched only the budget is invisible on the schedule page, and without a way out the reader
  // has no way to tell an empty history from a filtered one.
  const [showAll, setShowAll] = useState(false);
  const [mode, setMode] = useState<"changes" | "activity">("changes");

  const labels = { ...BASE_TRIGGER_LABELS, ...(triggerLabels ?? {}) };
  const scoped = !!sections?.length && !showAll;

  // Back to the page's own scope whenever it changes or the drawer reopens. Widening is a
  // deliberate act for one look; carrying it to the next page would silently un-scope a drawer
  // the reader expects to be about the page they are on.
  const scopeKey = (sections ?? []).join("|");
  useEffect(() => {
    setShowAll(false);
    setMode("changes");
  }, [scopeKey, open]);

  const shown = useMemo(
    () => filterBySections(versions, scoped ? sections : undefined),
    [versions, sections, scoped]
  );
  const hiddenByScope = versions.length - shown.length;

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 400 } }}>
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.75, height: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#232323" }}>{title}</Typography>
            {subtitle && (
              <Typography sx={{ fontSize: 12, color: "#8c9097" }}>{subtitle}</Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon sx={{ fontSize: 18, color: "#8c9097" }} />
          </IconButton>
        </Box>

        {!!activity && (
          <Box sx={{ display: "inline-flex", gap: 0.25, p: 0.25, borderRadius: 1.5, bgcolor: "#f1f2f6", alignSelf: "flex-start" }}>
            {(["changes", "activity"] as const).map((m) => (
              <Box
                key={m}
                component="button"
                type="button"
                onClick={() => setMode(m)}
                sx={{
                  border: "none",
                  borderRadius: 1.25,
                  px: 1.25,
                  py: 0.375,
                  cursor: "pointer",
                  bgcolor: mode === m ? "#fff" : "transparent",
                  boxShadow: mode === m ? "0 1px 2px rgba(16,24,40,0.08)" : "none",
                  color: mode === m ? "#413a52" : "#8c9097",
                  fontSize: 11.5,
                  fontWeight: 600,
                  textTransform: "capitalize",
                }}
              >
                {m}
              </Box>
            ))}
          </Box>
        )}

        {mode === "changes" && !!sections?.length && (
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 11.5, color: "#8c9097" }}>
              {scoped
                ? `Showing ${sections.join(" and ")} changes`
                : "Showing all changes"}
            </Typography>
            {(scoped ? hiddenByScope > 0 : true) && (
              <Box
                component="a"
                onClick={() => setShowAll((v) => !v)}
                sx={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "#845adf",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {scoped ? `Show all (${hiddenByScope} more)` : "Show only this page"}
              </Box>
            )}
          </Box>
        )}

        {mode === "activity" ? (
          activity!.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: "#8c9097", mt: 1 }}>
              Nothing has happened to this engagement yet.
            </Typography>
          ) : (
            <ActivityList entries={activity!} labels={labels} />
          )
        ) : shown.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: "#8c9097", mt: 1 }}>{emptyMessage}</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, overflowY: "auto" }}>
            {shown.map((v) => {
              const isExpanded = expanded.has(v.id);
              const hasChanges = v.changes.length > 0;
              const meta = renderMeta?.(v);
              return (
                <Box
                  key={v.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: v.is_current ? "1px solid #d9c9f5" : "1px solid #eceef2",
                    bgcolor: v.is_current ? "#faf8fe" : "#fff",
                  }}
                >
                  {/* Above the version number, because on a merged timeline "v2 · Current" is
                      only meaningful once you know which file it is the second version of. */}
                  {v.subject && <Subject text={v.subject} />}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#333335" }}>v{v.version_number}</Typography>
                      {v.is_current && (
                        <Box sx={{ px: 1, py: 0.25, borderRadius: 5, bgcolor: "#e7f7f1" }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#128a67" }}>Current</Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 12, color: "#8c9097" }}>{formatDate(v.created)}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: "#536485", mt: 0.75 }}>{v.change_summary}</Typography>
                  <Typography sx={{ fontSize: 12, color: "#8c9097", mt: 0.25 }}>
                    {attribution(v, labels)}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.75, gap: 1 }}>
                    <Typography sx={{ fontSize: 12, color: "#8c9097" }}>{meta}</Typography>
                    {hasChanges && (
                      <Box
                        component="a"
                        onClick={() => toggle(v.id)}
                        sx={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: "#845adf",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {isExpanded ? "Hide changes" : "Show changes"}
                      </Box>
                    )}
                  </Box>
                  {hasChanges && isExpanded && <VersionDiff changes={v.changes} />}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
